import { useState } from 'react';
import {
  TextField,
  Button,
  Card,
  Typography,
  Stack,
  Alert,
  CircularProgress,
  Box,
  Link,
  IconButton,
  InputAdornment
} from '@mui/material';
import GoogleAuthButton from './GoogleAuthButton';
import { safeReturnTo } from '../utils/googleAuth';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import { useAuthStore } from '../store/authStore';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';

export default function Login() {

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const [isLogin, setIsLogin] = useState(true);

  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [error, setError] = useState('');
  const [info, setInfo] = useState(() => searchParams.get('verified') === '1'
    ? 'Correo confirmado. Ya puedes iniciar sesión.'
    : searchParams.get('verified') === '0'
      ? ''
      : '');

  const { login, register, loading } = useAuthStore();

  const fromRoute = safeReturnTo(location.state?.from);
  const googleReturnTo = searchParams.get('from') === 'reserva' && searchParams.get('company')
    ? '/reservar/' + encodeURIComponent(searchParams.get('company')) : fromRoute;

  const handleSubmit = async (e) => {

    e.preventDefault();
    setError('');
    if (isLogin) setInfo('');

    if (!isLogin) {
      if (formData.password !== formData.confirmPassword) {
        setError('Las contraseñas no coinciden');
        return;
      }
      if (formData.password.length < 8) {
        setError('La contraseña debe tener al menos 8 caracteres');
        return;
      }
    }

    if (isLogin) {

      const res = await login(formData.email, formData.password);

      if (res.success) {

        const from = searchParams.get('from');
        const company = searchParams.get('company');

        if (from === 'reserva' && company) {
          navigate(`/reservar/${company}`, { replace: true });
        } else {
          navigate(fromRoute, { replace: true });
        }

      } else {
        setError(res.error);
      }

    } else {

      const res = await register({
        nombre: formData.nombre,
        apellido: formData.apellido,
        email: formData.email,
        password: formData.password
      });

      if (!res.success) {
        setError(res.error);
        return;
      }
      if (res.pendingVerification) {
        setIsLogin(true);
        setInfo(res.message || 'Revisa tu correo y confirma el enlace para activar tu cuenta.');
        return;
      }
    }
  };

  return (
    <Stack alignItems="center" justifyContent="center" minHeight="90vh">

      <Card
        sx={{
          p: 4,
          width: '100%',
          maxWidth: 400,
          borderRadius: 4,
          boxShadow: "0 8px 40px rgba(0,0,0,0.12)"
        }}
      >

        <Typography
          variant="h5"
          textAlign="center"
          fontWeight="800"
          gutterBottom
        >
          {isLogin ? 'BIENVENIDO' : 'ÚNETE AHORA'}
        </Typography>

        {info && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {info}
          </Alert>
        )}
        {searchParams.get('verified') === '0' && !info && (
          <Alert severity="error" sx={{ mb: 2 }}>
            El enlace no es válido o ya venció. Solicita uno nuevo al registrarte.
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack spacing={2} sx={{ mb: 3 }}>
          <GoogleAuthButton returnTo={googleReturnTo} onError={setError} disabled={loading} />
          <Typography variant="body2" textAlign="center" color="text.secondary">O continúa con tu correo</Typography>
        </Stack>

        <form onSubmit={handleSubmit}>

          <Stack spacing={2.5}>

            {!isLogin && (
              <Stack direction="row" spacing={1}>
                <TextField
                  label="Nombre"
                  fullWidth
                  required
                  value={formData.nombre}
                  onChange={e =>
                    setFormData({ ...formData, nombre: e.target.value })
                  }
                />
                <TextField
                  label="Apellido"
                  fullWidth
                  required
                  value={formData.apellido}
                  onChange={e =>
                    setFormData({ ...formData, apellido: e.target.value })
                  }
                />
              </Stack>
            )}

            <TextField
              label="Correo Electrónico"
              type="email"
              fullWidth
              required
              value={formData.email}
              onChange={e =>
                setFormData({ ...formData, email: e.target.value })
              }
            />

            <TextField
              label="Contraseña"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              required
              value={formData.password}
              onChange={e =>
                setFormData({ ...formData, password: e.target.value })
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            {!isLogin && (
              <TextField
                label="Confirmar Contraseña"
                type={showConfirmPassword ? 'text' : 'password'}
                fullWidth
                required
                error={
                  formData.confirmPassword &&
                  formData.password !== formData.confirmPassword
                }
                helperText={
                  formData.confirmPassword &&
                  formData.password !== formData.confirmPassword
                    ? 'Las contraseñas no coinciden'
                    : ''
                }
                value={formData.confirmPassword}
                onChange={e =>
                  setFormData({
                    ...formData,
                    confirmPassword: e.target.value
                  })
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            )}

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={
                loading ||
                (!isLogin &&
                  formData.password !== formData.confirmPassword)
              }
              sx={{
                py: 1.5,
                fontWeight: 'bold',
                borderRadius: 2
              }}
            >
              {loading
                ? <CircularProgress size={24} />
                : (isLogin ? 'INGRESAR' : 'CREAR CUENTA')}
            </Button>

            <Box textAlign="center">
              <Link
                component="button"
                type="button"
                variant="body2"
                underline="hover"
                onClick={() => {
                  setIsLogin(!isLogin);
                  setError('');
                  setInfo('');
                }}
              >
                {isLogin
                  ? '¿No tienes cuenta? Regístrate'
                  : '¿Ya tienes cuenta? Inicia sesión'}
              </Link>
            </Box>

          </Stack>

        </form>

      </Card>

    </Stack>
  );
}